import type * as Monaco from "monaco-editor";
import {
  toCompletionList,
  toDefinition,
  toDocumentSymbol,
  toFoldingRange,
  toHover,
  toLink,
  toMarkerData,
  toSelectionRanges,
  toTextEdit,
  fromPosition,
  fromRange,
  toCodeAction,
  fromCodeActionContext,
  toLocationLink
} from "monaco-languageserver-types";
import { registerMarkerDataProvider } from "monaco-marker-data-provider";
import {
  getLanguageService,
  type LanguageService,
  type LanguageSettings,
  type SchemasSettings
} from "yaml-language-server";
import { TextDocument } from "vscode-languageserver-textdocument";

export type MonacoInstance = typeof Monaco;

export type MonacoYamlOptions = {
  completion?: boolean;
  hover?: boolean;
  validate?: boolean;
  format?: boolean;
  customTags?: string[];
  schemas?: SchemasSettings[];
  enableSchemaRequest?: boolean;
  yamlVersion?: "1.1" | "1.2";
  isKubernetes?: boolean;
  schemaRequestService?: (uri: string) => Promise<string>;
};

export type MonacoYaml = {
  dispose: () => void;
  update: (options?: MonacoYamlOptions) => Promise<void>;
};

export type MonacoYamlTestApi = {
  doValidation: (model: Monaco.editor.ITextModel) => Promise<unknown[]>;
  doComplete: (
    model: Monaco.editor.ITextModel,
    position: Monaco.IPosition
  ) => Promise<unknown>;
  doHover: (
    model: Monaco.editor.ITextModel,
    position: Monaco.IPosition
  ) => Promise<unknown>;
  doDefinition: (
    model: Monaco.editor.ITextModel,
    position: Monaco.IPosition
  ) => Promise<unknown>;
  doFormat: (model: Monaco.editor.ITextModel) => Promise<unknown[]>;
  doOnTypeFormat: (
    model: Monaco.editor.ITextModel,
    position: Monaco.IPosition,
    ch: string,
    options: Monaco.languages.FormattingOptions
  ) => Promise<unknown[] | undefined>;
  findDocumentSymbols: (model: Monaco.editor.ITextModel) => Promise<unknown[]>;
  findDocumentLinks: (model: Monaco.editor.ITextModel) => Promise<unknown[]>;
  doCodeActions: (
    model: Monaco.editor.ITextModel,
    range: Monaco.IRange,
    context: Monaco.languages.CodeActionContext
  ) => Promise<unknown>;
  getFoldingRanges: (
    model: Monaco.editor.ITextModel,
    context: Monaco.languages.FoldingContext
  ) => Promise<unknown>;
  getSelectionRanges: (
    model: Monaco.editor.ITextModel,
    positions: Monaco.IPosition[]
  ) => Promise<unknown[]>;
};

export type JSONSchema = Record<string, unknown>;
export type { SchemasSettings };

type MonacoYamlOptionsResolved = Required<
  Pick<
    MonacoYamlOptions,
    | "completion"
    | "hover"
    | "validate"
    | "format"
    | "customTags"
    | "schemas"
    | "enableSchemaRequest"
    | "yamlVersion"
    | "isKubernetes"
  >
> &
  Pick<MonacoYamlOptions, "schemaRequestService">;

type ServiceState = {
  update: (options?: MonacoYamlOptions) => Promise<void>;
  dispose: () => void;
  getTestApi: () => MonacoYamlTestApi;
};

const services = new WeakMap<MonacoInstance, ServiceState>();

const defaultOptions: MonacoYamlOptionsResolved = {
  completion: true,
  hover: true,
  validate: true,
  format: true,
  customTags: [],
  schemas: [],
  enableSchemaRequest: false,
  yamlVersion: "1.2",
  isKubernetes: false,
  schemaRequestService: undefined
};

export function configureMonacoYaml(
  monaco: MonacoInstance,
  options?: MonacoYamlOptions
): MonacoYaml {
  return registerMonacoYaml(monaco, options);
}

export function registerMonacoYaml(
  monaco: MonacoInstance,
  options?: MonacoYamlOptions
): MonacoYaml {
  const existing = services.get(monaco);
  if (existing) {
    void existing.update(options);
    return {
      dispose: () => existing.dispose(),
      update: (next) => existing.update(next)
    };
  }

  const state = createService(monaco, options);
  services.set(monaco, state);

  return {
    dispose: () => {
      state.dispose();
      services.delete(monaco);
    },
    update: (next) => state.update(next)
  };
}

export function __getMonacoYamlTestApi(
  monaco: MonacoInstance
): MonacoYamlTestApi | null {
  const state = services.get(monaco);
  return state ? state.getTestApi() : null;
}

function createService(
  monaco: MonacoInstance,
  options?: MonacoYamlOptions
): ServiceState {
  let resolvedOptions = resolveOptions(options);
  const yamlLanguageId = "yaml";

  registerYamlLanguage(monaco, yamlLanguageId);

  const languageService = getLanguageService({
    schemaRequestService: (uri: string) =>
      schemaRequest(resolvedOptions, uri),
    workspaceContext: {
      resolveRelativePath: (relativePath: string, resource: string) =>
        resolveRelativePath(monaco, relativePath, resource)
    }
  });

  const adapter = new YamlLanguageServiceAdapter(
    monaco,
    languageService,
    () => resolvedOptions
  );

  const markerProvider = registerMarkerDataProvider(monaco, yamlLanguageId, {
    owner: "yaml",
    provideMarkerData: async (model) => {
      if (!resolvedOptions.validate) {
        return [];
      }
      const diagnostics = await adapter.doValidation(model);
      return diagnostics.map(toMarkerData);
    },
    doReset: (model) => {
      adapter.resetSchema(model.uri.toString());
    }
  });

  const disposables: Monaco.IDisposable[] = [];

  disposables.push(
    monaco.languages.registerCompletionItemProvider(yamlLanguageId, {
      triggerCharacters: [":", " "],
      provideCompletionItems: async (model, position) => {
        if (!resolvedOptions.completion) {
          return { suggestions: [] };
        }
        const list = await adapter.doComplete(model, position);
        const word = model.getWordUntilPosition(position);
        const range = {
          startLineNumber: position.lineNumber,
          endLineNumber: position.lineNumber,
          startColumn: word.startColumn,
          endColumn: word.endColumn
        };
        return toCompletionList(list, { range });
      }
    })
  );

  disposables.push(
    monaco.languages.registerHoverProvider(yamlLanguageId, {
      provideHover: async (model, position) => {
        if (!resolvedOptions.hover) {
          return null;
        }
        const hover = await adapter.doHover(model, position);
        return hover ? toHover(hover) : null;
      }
    })
  );

  disposables.push(
    monaco.languages.registerDefinitionProvider(yamlLanguageId, {
      provideDefinition: async (model, position) => {
        const definition = await adapter.doDefinition(model, position);
        if (!definition) {
          return null;
        }
        if (Array.isArray(definition) && "targetUri" in definition[0]) {
          return definition.map((link) => toLocationLink(link));
        }
        return toDefinition(definition as any);
      }
    })
  );

  disposables.push(
    monaco.languages.registerDocumentSymbolProvider(yamlLanguageId, {
      provideDocumentSymbols: async (model) => {
        const symbols = await adapter.findDocumentSymbols(model);
        return symbols.map((symbol) => toDocumentSymbol(symbol));
      }
    })
  );

  disposables.push(
    monaco.languages.registerLinkProvider(yamlLanguageId, {
      provideLinks: async (model: Monaco.editor.ITextModel) => {
        const links = await adapter.findDocumentLinks(model);
        return {
          links: links.map((link) => toLink(link))
        };
      }
    })
  );

  disposables.push(
    monaco.languages.registerDocumentFormattingEditProvider(yamlLanguageId, {
      provideDocumentFormattingEdits: async (model, _options) => {
        if (!resolvedOptions.format) {
          return [];
        }
        const edits = await adapter.doFormat(model);
        return edits.map((edit) => toTextEdit(edit));
      }
    })
  );

  disposables.push(
    monaco.languages.registerOnTypeFormattingEditProvider(yamlLanguageId, {
      autoFormatTriggerCharacters: ["\n"],
      provideOnTypeFormattingEdits: async (model, position, ch, options) => {
        if (!resolvedOptions.format) {
          return [];
        }
        const edits = await adapter.doOnTypeFormat(model, position, ch, options);
        return edits ? edits.map((edit) => toTextEdit(edit)) : [];
      }
    })
  );

  disposables.push(
    monaco.languages.registerCodeActionProvider(yamlLanguageId, {
      provideCodeActions: async (model, range, context) => {
        const actions = await adapter.doCodeActions(model, range, context);
        if (!actions) {
          return { actions: [], dispose: () => {} };
        }
        return {
          actions: actions.map((action) => toCodeAction(action)),
          dispose: () => {}
        };
      }
    })
  );

  disposables.push(
    monaco.languages.registerFoldingRangeProvider(yamlLanguageId, {
      provideFoldingRanges: async (model, context) => {
        const ranges = await adapter.getFoldingRanges(model, context);
        return ranges ? ranges.map((range) => toFoldingRange(range)) : null;
      }
    })
  );

  disposables.push(
    monaco.languages.registerSelectionRangeProvider(yamlLanguageId, {
      provideSelectionRanges: async (model, positions) => {
        const ranges = await adapter.getSelectionRanges(model, positions);
        return ranges.map((range) => toSelectionRanges(range));
      }
    })
  );

  const update = async (next?: MonacoYamlOptions) => {
    const merged: MonacoYamlOptions = {
      ...resolvedOptions,
      ...next
    };
    resolvedOptions = resolveOptions(merged);
    applyLanguageSettings(languageService, resolvedOptions);
    await markerProvider.revalidate();
  };

  void update(options);

  return {
    update,
    dispose: () => {
      for (const disposable of disposables) {
        disposable.dispose();
      }
      markerProvider.dispose();
    },
    getTestApi: () => ({
      doValidation: adapter.doValidation.bind(adapter),
      doComplete: adapter.doComplete.bind(adapter),
      doHover: adapter.doHover.bind(adapter),
      doDefinition: adapter.doDefinition.bind(adapter),
      doFormat: adapter.doFormat.bind(adapter),
      doOnTypeFormat: adapter.doOnTypeFormat.bind(adapter),
      findDocumentSymbols: adapter.findDocumentSymbols.bind(adapter),
      findDocumentLinks: adapter.findDocumentLinks.bind(adapter),
      doCodeActions: adapter.doCodeActions.bind(adapter),
      getFoldingRanges: adapter.getFoldingRanges.bind(adapter),
      getSelectionRanges: adapter.getSelectionRanges.bind(adapter)
    })
  };
}

function resolveOptions(options?: MonacoYamlOptions): MonacoYamlOptionsResolved {
  return {
    ...defaultOptions,
    ...options,
    customTags: options?.customTags ?? defaultOptions.customTags,
    schemas: options?.schemas ?? defaultOptions.schemas
  };
}

function applyLanguageSettings(
  languageService: LanguageService,
  options: MonacoYamlOptionsResolved
): void {
  const settings: LanguageSettings = {
    completion: options.completion,
    hover: options.hover,
    validate: options.validate,
    format: options.format,
    schemas: options.schemas,
    customTags: options.customTags,
    yamlVersion: options.yamlVersion,
    isKubernetes: options.isKubernetes
  };
  languageService.configure(settings);
}

function schemaRequest(
  options: MonacoYamlOptionsResolved,
  uri: string
): Promise<string> {
  if (options.schemaRequestService) {
    return options.schemaRequestService(uri);
  }
  if (!options.enableSchemaRequest) {
    return Promise.reject(new Error("Schema request service is disabled."));
  }
  if (typeof fetch !== "function") {
    return Promise.reject(new Error("Global fetch is not available."));
  }
  return fetch(uri).then((response) => {
    if (!response.ok) {
      throw new Error(`Schema request failed: ${response.status} ${response.statusText}`);
    }
    return response.text();
  });
}

function resolveRelativePath(
  monaco: MonacoInstance,
  relativePath: string,
  resource: string
): string {
  const uri = monaco.Uri?.parse ? monaco.Uri.parse(resource) : null;
  if (!uri || !monaco.Uri?.joinPath) {
    return relativePath;
  }
  return monaco.Uri.joinPath(uri, "..", relativePath).toString();
}

function registerYamlLanguage(monaco: MonacoInstance, languageId: string): void {
  const languages = monaco.languages.getLanguages?.() ?? [];
  if (!languages.some((lang) => lang.id === languageId)) {
    monaco.languages.register({
      id: languageId,
      extensions: [".yaml", ".yml"],
      aliases: ["YAML", "yaml"]
    });
  }
  monaco.languages.setLanguageConfiguration?.(languageId, {
    comments: { lineComment: "#" },
    brackets: [
      ["{", "}"],
      ["[", "]"]
    ],
    autoClosingPairs: [
      { open: "{", close: "}" },
      { open: "[", close: "]" },
      { open: "\"", close: "\"" },
      { open: "'", close: "'" }
    ],
    surroundingPairs: [
      { open: "{", close: "}" },
      { open: "[", close: "]" },
      { open: "\"", close: "\"" },
      { open: "'", close: "'" }
    ]
  });
}

class YamlLanguageServiceAdapter {
  private documents = new Map<string, { version: number; document: TextDocument }>();

  constructor(
    private monaco: MonacoInstance,
    private languageService: LanguageService,
    private getOptions: () => MonacoYamlOptionsResolved
  ) {}

  resetSchema(uri: string): void {
    this.languageService.resetSchema(uri);
  }

  async doValidation(model: Monaco.editor.ITextModel) {
    const doc = this.getDocument(model);
    return this.languageService.doValidation(doc, this.getOptions().isKubernetes);
  }

  async doComplete(model: Monaco.editor.ITextModel, position: Monaco.IPosition) {
    const doc = this.getDocument(model);
    return this.languageService.doComplete(
      doc,
      fromPosition(position),
      this.getOptions().isKubernetes
    );
  }

  async doHover(model: Monaco.editor.ITextModel, position: Monaco.IPosition) {
    const doc = this.getDocument(model);
    return this.languageService.doHover(doc, fromPosition(position));
  }

  async doDefinition(model: Monaco.editor.ITextModel, position: Monaco.IPosition) {
    const doc = this.getDocument(model);
    return this.languageService.doDefinition(doc, {
      textDocument: { uri: doc.uri },
      position: fromPosition(position)
    });
  }

  async doFormat(model: Monaco.editor.ITextModel) {
    const doc = this.getDocument(model);
    return this.languageService.doFormat(doc);
  }

  async doOnTypeFormat(
    model: Monaco.editor.ITextModel,
    position: Monaco.IPosition,
    ch: string,
    options: Monaco.languages.FormattingOptions
  ) {
    const doc = this.getDocument(model);
    return this.languageService.doDocumentOnTypeFormatting(doc, {
      textDocument: { uri: doc.uri },
      position: fromPosition(position),
      ch,
      options: {
        tabSize: options.tabSize,
        insertSpaces: options.insertSpaces
      }
    });
  }

  async findDocumentSymbols(model: Monaco.editor.ITextModel) {
    const doc = this.getDocument(model);
    return this.languageService.findDocumentSymbols2(doc);
  }

  async findDocumentLinks(model: Monaco.editor.ITextModel) {
    const doc = this.getDocument(model);
    return this.languageService.findLinks(doc);
  }

  async doCodeActions(
    model: Monaco.editor.ITextModel,
    range: Monaco.IRange,
    context: Monaco.languages.CodeActionContext
  ) {
    const doc = this.getDocument(model);
    return this.languageService.getCodeAction(doc, {
      textDocument: { uri: doc.uri },
      range: fromRange(range),
      context: fromCodeActionContext(context)
    });
  }

  async getFoldingRanges(
    model: Monaco.editor.ITextModel,
    _context: Monaco.languages.FoldingContext
  ) {
    const doc = this.getDocument(model);
    return this.languageService.getFoldingRanges(doc, {
      lineFoldingOnly: true
    });
  }

  async getSelectionRanges(
    model: Monaco.editor.ITextModel,
    positions: Monaco.IPosition[]
  ) {
    const doc = this.getDocument(model);
    return this.languageService.getSelectionRanges(
      doc,
      positions.map((position) => fromPosition(position))
    );
  }

  private getDocument(model: Monaco.editor.ITextModel): TextDocument {
    const uri = model.uri.toString();
    const version = model.getVersionId();
    const cached = this.documents.get(uri);
    if (cached && cached.version === version) {
      return cached.document;
    }
    const document = TextDocument.create(
      uri,
      model.getLanguageId(),
      version,
      model.getValue()
    );
    this.documents.set(uri, { version, document });
    return document;
  }
}
